import { computed, Injectable, signal } from '@angular/core';
import {
  catchError, interval, map, mergeMap,
  Observable, of, retry, startWith,
  Subscription,
  switchMap, take, tap, throwError
} from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ResponseModel } from "../models/response.model";
import { UserService } from "./user.service";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { TrackingHelperService } from "./tracking-helper.service";
import { ConfigService } from "./config.service";
import { ChatService } from "./chat.service";
import { PromotionModel } from "../models/promotion.model";
import { ErrorMapperService } from "./error-mapper.service";
import { PromotionService } from "./promotion.service";
import { SessionInfo, UserModel, UserTrackingData } from "../models/user.model";
import * as Sentry from "@sentry/browser";
import { LanguageService } from "./language.service";
import { MessageService } from "./message.service";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})


export class AuthService {
  /**
   * handles login AND logged-in user related things
   * at initialization checks if the user is still logged in - getUserInfo (if user refreshes the page or navigates away)
   * stores authToken (and cashierUrl) for logged-in user
   * keeps user logged in = keepAlive in every minute (batch call for balance, currentPromo & unreadMessages etc.)
   * logs out user from server: logOut
   * logs out user from frontend: resetUserSession
   * handles password resetting resetUserPassword
   * */



  readonly tokenSignal = signal<string | null>(null);
  readonly cashierUrlSignal = signal<string | null>(null);
  readonly authState = computed<'guest' | 'loading' | 'loggedIn'>(() => {
    const token = this.tokenSignal();
    const user = this.userService.user();
    if (!token) return 'guest';
    if (token && !user) return 'loading';
    if (token && user) return 'loggedIn';
    return 'guest'
  })

  private keepAliveSub?: Subscription;
  //for flipping API: old token to keep transaction history
  private legacyKeepAliveSub?: Subscription;
  readonly legacyTokenSignal = signal<string | null>(null);


  constructor(
    private http: HttpClient,
    private configs: ConfigService,
    private userService: UserService,
    private languageService: LanguageService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private gaService: GoogleAnalyticsService,
    private trackingService: TrackingHelperService,
    private errorMapper: ErrorMapperService,
    private promoService: PromotionService,
    private messageService: MessageService,
    private chatService: ChatService
  ) {
    /** INITIALIZATION
     if user is logged in (refreshed the page, navigated away, etc)
     - set sessionToken and cashierUrl from localStorage
     - get userInfo: to see if the token is still valid (if yes, we get the userObjected in userService and we start the keepalive
     - initializing chats
     */

    this.restoreTokenAndUrlFromLocalStorage();


    if (this.tokenSignal()) {
      this.userService.getUserInfo().pipe(
        take(1),
        catchError(err => throwError(() => this.errorMapper.mapAPIerrors(err)))
      ).subscribe({
        next: (user) => {
          if (user) {
            //we have the user logged in and the token is still valid (ex. page reloaded/refreshed)
            this.startKeepAlive();
            this.chatService.initializeChat(user, this.tokenSignal() ?? undefined);
          }
        },
        error: (err) => {
          console.error('Could not fetch userDetails while restoring page', err);
          switch (err.type) {
            case "SESSION":
              this.resetUserSession();
              this.toastr.error(this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE'), this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_H1'), { toastClass: 'ngx-toastr lobbyless-toast' });
              break;
            case "USER":
              this.toastr.error(err.message, this.translate.instant('LOGIN.TOASTR_ERROR_NOUSERINFO_MESSAGE'), { toastClass: 'ngx-toastr lobbyless-toast' })
              break;
            default:
              //system errors - network error or go to CS
              this.toastr.error(
                this.translate.instant(`${err.message ?? 'LOGIN.TOASTR_ERROR_LOGGEDOUT_TIP'}`),
                this.translate.instant('LOGIN.TOASTR_ERROR_NOUSERINFO_MESSAGE'),
                { toastClass: 'ngx-toastr lobbyless-toast' });
          }

        }
      }
      )
    }

    //react to backend Flips
    this.configs.apiFlipped$.subscribe((data) => {

      const oldToken = this.tokenSignal();
      if (oldToken) {
        this.legacyTokenSignal.set(oldToken);
        try {
          localStorage.setItem('legacySessionToken', oldToken);
        } catch (error) {
          console.warn('Error accessing localStorage while saving legacy token:', error);
        }
        this.startLegacyKeepAlive(oldToken);
      }

      //stop old system things : stop keepalive & close previous chat
      this.stopKeepAlive();
      this.chatService.handleChatFlip();

      //flip token
      try {
        localStorage.setItem('sessionToken', data.token);
      } catch (error) {
        console.warn('Error accessing localStorage while flipping APIs:', error);
      }
      this.tokenSignal.set(data.token);
      this.cashierUrlSignal.set(null) //we dont need on gp brands

      // Momentarily wipe out UI state to trigger loaders and GameService logic
      this.userService.updateUser(null);
      this.userService.updateBalance(null);
      this.promoService.updateCurrentPromo(null);
      this.promoService.updatePromos(null);

      // Refetch user profile using the new GP API endpoints
      this.userService.getUserInfo().pipe(
        take(1),
        catchError(err => throwError(() => this.errorMapper.mapAPIerrors(err)))
      ).subscribe({
        next: (user) => {
          if (user) {
            this.startKeepAlive();
            //initilaize chat
            this.chatService.initializeChat(user, this.tokenSignal() ?? undefined);
          }
        },
        error: (err) => {
          //todo work out a correct way of handling this case
          console.error('Could not fetch userDetails after backend flip', err);
          this.resetUserSession();
          this.toastr.error('Session reset due to flipped server error.', 'Unexpected Error', { toastClass: 'ngx-toastr lobbyless-toast' });
        }
      });
    });
  }

  /** LOGIN
   * from log in modal OR after successful registration (regStep4, minRegModal)
   * gets token and cashierUrl from API
   * gets userInfo - this is after what we can do anything else
   * starts keepAlive
   * initializes chat
   * */

  login(userLog: SessionInfo): Observable<UserModel> {
    const initiallyRival = this.configs.isRivalCasino;

    //close chat if it was opened as guest user
    this.chatService.toggleChat(false);

    //send data domain and cashier link in login request
    userLog.domain = window.location.origin;
    userLog.cashier = `${userLog.domain}/account/banking`;

    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "session.login", "params": userLog })
      .pipe(
        map((loginResponse: ResponseModel) => {
          if ('result' in loginResponse) {
            return loginResponse.result as { session: string; cashierUrl: string };
          } else {
            throw loginResponse
          }
        }),
        tap(result => {
          if (result.session) {
            try {
              localStorage.setItem('sessionToken', result.session);
            } catch (error) {
              console.warn('Error accessing localStorage while login:', error);
            }
            this.tokenSignal.set(result.session)
          }

          if (result.cashierUrl) {
            try {
              localStorage.setItem('cashierUrl', result.cashierUrl);
            } catch (error) {
              console.warn('Error accessing localStorage while login:', error);
            }
            this.cashierUrlSignal.set(result.cashierUrl)
          }
        }),
        switchMap(() => this.userService.getUserInfo()),
        tap(user => {
          if (user) {
            this.gaService.event('login', undefined, undefined, undefined, undefined, {
              user_id: user.login,
              user_uuid: user.id, ...this.trackingService.getAffiliateOptions()
            });

            Sentry.setUser({
              id: user.id,
              username: user.login,
              tracker: this.trackingService.getTracker()
            })

            if (user.locale) {
              const langFromLocale = user.locale.slice(0, 2);
              this.languageService.setUserSelectedLang(langFromLocale);
            }

            const message = this.translate.instant('LOGIN.TOASTR_SUCCESS_MESSAGE', { username: (user.fname ?? user.login) });
            const title = this.translate.instant('LOGIN.TOASTR_SUCCESS_H1');
            this.toastr.success(message, title, { toastClass: 'ngx-toastr lobbyless-toast' });

            this.userService.handleEmailVerification(user);
          }
        }),
        switchMap(user => {
          if (user && this.configs.isRivalCasino) {
            const trackingData: UserTrackingData = {
              ...user,
              merchant_id: this.configs.casinoId,
              password: userLog.password,
              auth: this.tokenSignal() ?? undefined,
              domain: userLog.domain,
              cashier: userLog.cashier
            }
            return this.trackingService.sendTrackingData(trackingData).pipe(map(() => user));
          }
          return of(user);
        }),
        tap(user => {
          if (user) {
            // If a flip happened during the tracking process, the constructor's apiFlipped$ subscription already handled the initialization. We should skip it here to avoid 429 errors.
            if (initiallyRival && !this.configs.isRivalCasino) return;

            this.startKeepAlive();
            this.chatService.initializeChat(user, this.tokenSignal() ?? undefined);
          }
        }),
        catchError(err => throwError(() => this.errorMapper.mapAPIerrors(err)))
      )
  }


  resetPassword(userLog: { email: string }): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.resetPassword", "params": userLog })
      .pipe(
        map((response: ResponseModel) => {
          if ('error' in response) {
            throw response;
          }
          return response
        }
        ),
        catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
      )
  }

  //"new" keepalive having balance and current promo inside in a batch jsonrpc call
  startKeepAlive() {
    this.keepAliveSub?.unsubscribe();
    this.keepAliveSub = interval(60 * 1000) // refreshes user balance & current promo in every minute
      .pipe(
        startWith(0),
        switchMap(() => {
          // Build batch request
          const batchRequest: any[] = [
            { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "session.keepAlive", "params": {} },
            { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "promotion.current", "params": {} }
          ];

          return this.http.post<ResponseModel[]>(`${this.configs.apiUrl}`, batchRequest)
            .pipe(
              retry({ count: 3, delay: 2000 }), //retries every 2 second when there is an error with NOT 200 OK status code - if no success, then keepAlive is ended
              mergeMap(responses => {
                //normalize responses from GP (coming from string[], not parsed)
                const normalizedResponses = this.errorMapper.normalizeBatchResponse(responses);

                let balance;
                let compBalance;
                let hasUnreadMessage;
                let activePromo;

                for (const res of normalizedResponses) {
                  if ('result' in res && 'balance' in res.result) {
                    balance = res.result.balance;
                  }
                  if ('result' in res && 'compBalance' in res.result) {
                    compBalance = res.result.compBalance;
                  }
                  if ('result' in res && 'hasUnreadMessage' in res.result) {
                    hasUnreadMessage = res.result.hasUnreadMessage;
                  }

                  if ('result' in res && 'promotion' in res.result) {
                    activePromo = res.result.promotion;
                  }
                }

                if (balance !== undefined && activePromo !== undefined) {
                  return of({ balance, activePromo, compBalance, hasUnreadMessage })
                }
                return throwError(() => normalizedResponses);
                //todo later: handle errors separately, and use the value we have from one call

              }),
              catchError(err => throwError(() => this.errorMapper.mapKeepAliveError(err)))
            )
        })
      )
      .subscribe({
        next: (response) => {
          // console.log('KA started');
          const balance = response.balance as number;
          if (balance !== undefined) {
            this.userService.updateBalance(balance);
            this.gaService.event('balance', undefined, undefined, undefined, undefined, { user_balance: balance, ...this.trackingService.getAffiliateOptions() });
          }

          const compBalance = response.compBalance as number | undefined;
          this.userService.updateCompBalance(compBalance !== undefined ? compBalance : (this.userService.user()?.compBalance ?? null));

          const hasUnreadMessage = response.hasUnreadMessage as boolean | undefined;
          if (hasUnreadMessage !== undefined) {
            this.messageService.updateHasUnreadSignal(hasUnreadMessage);
          }
          // this.messageService.updateHasUnreadSignal(true); //this is for testing pop up modal

          const activePromo = response.activePromo as PromotionModel;
          if (activePromo !== undefined) {
            this.promoService.updateCurrentPromo(activePromo);
          }

        },
        error: err => {
          // console.error('Could not fetch balance', err)
          switch (err.type) {
            case 'SESSION':
              this.resetUserSession();
              this.toastr.error(this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_MESSAGE'), `${this.translate.instant('LOGIN.TOASTR_ERROR_LOGGEDOUT_H1')}`, { toastClass: 'ngx-toastr lobbyless-toast' })
              break;

            case 'USER': //error in response from server
            //todo later - these are not fatal errors => polling could continue, could update with the result from the not failed method
            //until that: treated as system error
            case 'SYSTEM':
              //balance is the last value we had - or user balance if we had + nice warning to tell them to try later
              const latestBalance = this.userService.balance();
              if (!latestBalance) {
                this.userService.updateBalance(this.userService.user()?.balance ?? null);
              }
              //compBalance and hasUnreadMessage are not updated on error

              //we dont do anything with the latest ActivePromo on error
              this.toastr.error(`${err.message ?? ''} ${this.translate.instant('ACCOUNT.BALANCE_FAILED_TIP')}`, `${this.translate.instant('ACCOUNT.BALANCE_FAILED_MESSAGE')}`, { toastClass: 'ngx-toastr lobbyless-toast' })
              break;
            default:
              console.error(err.raw);
              break;
          }

        }

      });
  }

  startLegacyKeepAlive(legacyToken: string) {
    const legacyCasinoId = environment.casinoId;
    const legacyApiUrl = environment.apiUrl ?? `https://www.casinocontroller.com/${environment.rivalName}/api-player/v3/jsonrpc`;

    this.legacyKeepAliveSub?.unsubscribe();
    this.legacyKeepAliveSub = interval(60 * 1000)
      .pipe(
        startWith(0),
        switchMap(() => {
          return this.http.post<ResponseModel[]>(legacyApiUrl, { "jsonrpc": "2.0", "id": legacyCasinoId, "method": "session.keepAlive", "params": {} }, {
            headers: {
              'Authorization': `Bearer ${legacyToken}`
            }
          })
            .pipe(
              retry({ count: 3, delay: 2000 }),
              map(response => this.errorMapper.normalizeBatchResponse([response])),
              catchError(err => throwError(() => this.errorMapper.mapKeepAliveError(err)))
            )
        })
      )
      .subscribe({
        next: () => {
          // Legacy session kept alive, we do not use incoming data
        },
        error: err => {
          if (err.type === 'SESSION') {
            this.stopLegacyKeepAlive();
            this.legacyTokenSignal.set(null);
            try {
              localStorage.removeItem('legacySessionToken');
            } catch (e) { }
          }
          //other errors fail silently
        }
      });
  }

  stopLegacyKeepAlive() {
    if (this.legacyKeepAliveSub) {
      this.legacyKeepAliveSub.unsubscribe();
    }
  }

  stopKeepAlive() {
    if (this.keepAliveSub) {
      this.keepAliveSub.unsubscribe();
      // console.log('KA stopped');
    }
  }


  logout(): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "session.logout", "params": {} })
      .pipe(
        map((response: ResponseModel) => {
          if ('error' in response) {
            throw response;
          }
          return response
        }
        ),
        catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
      )
  }

  resetUserSession(): void {
    //token expired (on 401 error) or successful log out
    this.stopKeepAlive();
    this.stopLegacyKeepAlive();
    this.legacyTokenSignal.set(null);

    try {
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('legacySessionToken');
      localStorage.removeItem('cashierUrl');
      localStorage.removeItem('flippedAPI');
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }

    this.tokenSignal.set(null);
    this.cashierUrlSignal.set(null);

    this.userService.updateUser(null); //this updates = resets compBalance as well
    this.userService.updateBalance(null);
    this.messageService.clearMessageState();
    this.promoService.updateCurrentPromo(null);
    this.promoService.updatePromos(null);

    //GA tracking
    this.gaService.event('logout', undefined, undefined, undefined, undefined, { ...this.trackingService.getAffiliateOptions() });

    this.chatService.logout();

    //TODO flip back API on logout - currently not used
    // this.configs.unflipAPI();

  }

  private restoreTokenAndUrlFromLocalStorage() {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      if (sessionToken) {
        this.tokenSignal.set(sessionToken);
      }

      const legacySessionToken = localStorage.getItem('legacySessionToken');
      if (legacySessionToken) {
        this.legacyTokenSignal.set(legacySessionToken);
        this.startLegacyKeepAlive(legacySessionToken);
      }

      //later: no need to save cashierUrl in localStorage (will use lobbyless-cashier) - todo
      const cashierUrl = localStorage.getItem('cashierUrl');
      if (cashierUrl) {
        this.cashierUrlSignal.set(cashierUrl);
      }
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
  }

  //edit url for rival iframes:
  addExtraInfoToIframeUrl(url: string, routeParam?: string): string {
    if (!this.configs.isRivalCasino) return url;

    //disable chat in iframes
    url += '&chatButton=0';

    //add btag and tracker to url
    let bTag = this.trackingService.getBannerTag();
    if (bTag) {
      url += `&btag=${bTag}`
    }
    let t = this.trackingService.getTracker();
    if (t) {
      url += `&t=${t}&tracker=${t}`
    }

    //add route params on profile
    if (routeParam) {
      if (url.includes('route')) {
        return url.replace(/route=[^&]*/, `route=${routeParam}`);
      } else {
        return url + `&route=${routeParam}`
      }
    }

    //set language
    const lang = this.languageService.currentLang();
    if (url.includes('lang')) {
      return url.replace(/lang=[^&]*/, `lang=${lang}`);
    } else {
      return url + `&lang=${lang}`
    }

  }

}
